import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, RouterModule, RouteReuseStrategy } from '@angular/router';

import { NotificationService } from 'src/app/services/notifications.service';
import { BulkDownloadService } from 'src/app/services/bulk-download.service';
import { TypeOfResult } from 'src/app/models/typeOfResult/typeOfResult';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent, RowSelectedEvent } from 'ag-grid-community';
import { baseColDef } from '../../utils';
import { FormControl, FormGroup } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { debounceTime, of, Subscription, switchMap, tap } from 'rxjs';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { TextTooltipRenderer } from '../../components/text-tooltip-cell-renderer/text-tooltip-cell-renderer.component';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { AgGridWrapperComponent } from '../../components/ag-grid-wrapper/ag-grid-wrapper.component';
import { SearchSortComponent } from '../../components/search-sort/search-sort.component';
import {MatBadgeModule} from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
@Component({
    selector: 'app-mbr-request-list',
    templateUrl: './notifications-list.component.html',
    styleUrls: ['./notifications-list.component.scss'],
    standalone: true,
    imports: [
      MatSidenavModule,
      RouterModule,
      MatIconModule,
      AgGridWrapperComponent,
      MatPaginatorModule,
      SearchSortComponent,
      MatBadgeModule,
      MatButtonModule
    ]
})
export class NotificationsListComponent implements OnInit {
  isLoading: boolean = false;
  submissionService: any;
  countSelected = 0;
  selectedRowIds: number[] = [];
  public rowData: any[];

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  public columnDefs: ColDef[] = [
    {
      ...baseColDef,
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 50,
    },
    {
      ...baseColDef,
      field: 'title',
      headerName: 'Title',
      flex:2,
      cellRenderer: TextTooltipRenderer,
      cellRendererParams: {
        onClick: this.onView.bind(this)
      }
    },
    {
      ...baseColDef,
      field: 'description', headerName: 'Description', flex:4
    },
    {
      ...baseColDef,
      field: 'dateTime', headerName: 'Date', flex:4,
      cellRenderer: (data: { value: string }) => {
        return new Date(data.value).toLocaleDateString();
      },
    },
  ];

  pageSettings: {
    defaultPageSize: number;
    pageSizeOptions: number[];
  } = {
    defaultPageSize: 20,
    pageSizeOptions: [20, 50, 100],
  };
  datePipe = new DatePipe('en-US');
  currentPage: number = 0;
  pageSize: number = this.pageSettings.defaultPageSize;
  subscriptions: Subscription[] = [];
  searchControl: FormControl = new FormControl('');
  dataToShow: any[];

  currentSortField = {
    sortValue: '',
  };
  currentIsAscending = false;

  totalCount: number = 0;
  searchKey: string = '';

  sortForm: FormGroup = new FormGroup({
    sortField: new FormControl({
      sortValue: this.currentSortField.sortValue,
    }),
    isAscending: new FormControl(this.currentIsAscending),
  });

  sortOptions = [
    { label: 'Date', sortValue: 'date' },
  ];

  gridAPI: GridApi;

  constructor(
    private service: NotificationService,
    private router: Router,
    private routeReuseStrategy: RouteReuseStrategy,
    private bulkDownloadService: BulkDownloadService,
  ) {}

  ngOnInit(): void {
      const searchControlSubscription = this.searchControl.valueChanges
        .pipe(
          debounceTime(300),
          tap(() => {
            this.currentPage = 0;
            this.reload();
          }),
        )
        .subscribe();

      this.subscriptions.push(searchControlSubscription);
  }

  onView(data: any) {
    this.service.markAsRead([data.id]).subscribe((_) => {
      this.service.notifyBellToUpdate();

      if (data.entity === TypeOfResult.BulkDownloadJob) {
        this.bulkDownloadService.downloadIfReady(data.referenceId);
        return;
      }

      const url = this.service.navigate(data.entity, data.referenceId, data.documentTypeId);
      const defaultReuseStrategy = this.routeReuseStrategy.shouldReuseRoute.bind(this.routeReuseStrategy);
      this.routeReuseStrategy.shouldReuseRoute = () => false;
      this.router.navigateByUrl(url).then(() => {
        this.routeReuseStrategy.shouldReuseRoute = defaultReuseStrategy;
      });
    });
  }

  onGridReady(params: GridReadyEvent) {
    this.gridAPI = params.api;
    this.reload();
  }

  handlePageEvent(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.reload();
  }

  private reload() {
    this.isLoading = true;
    this.searchKey = this.searchControl.value?.toLowerCase() || '';

    const serviceSubscription = this.service
        .get({
          sortColumn: this.currentSortField.sortValue,
          sortOrder: this.currentIsAscending ? 'asc' : 'desc',
          page: this.currentPage + 1,
          pageSize: this.pageSize,
          searchKey: this.searchKey
        }).pipe(
        switchMap((response: any) => {
          if (!response.isError) {
            return of(response.data);
          } else {
            return of([]);
          }
        })
      )
      .subscribe((data) => {
        this.rowData = data.items;
        this.totalCount = data.totalCount;
      });

      this.subscriptions.push(serviceSubscription);
  }

  setSortField() {
    const { sortField, isAscending } = this.sortForm.value;
    this.currentIsAscending = isAscending;
    this.currentSortField = sortField;

    this.reload();
  }

  resetSortField() {
    this.sortForm.setValue({
      sortField: this.currentSortField,
      isAscending: this.currentIsAscending,
    });
  }

  onSelection(event: RowSelectedEvent) {
    this.countSelected = event.api.getSelectedRows().length;
    this.selectedRowIds = event.api.getSelectedRows().map(x => x.id);
  }

  onMarkAsRead() {
    this.service.markAsRead(this.selectedRowIds).subscribe(_=> {
      this.reload();
      this.countSelected = 0;

      this.service.notifyBellToUpdate();
    })
  }
}
